import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FormField } from '@/components/forms/FormField'
import { Select } from '@/components/ui/Select'
import { classesQuery } from '@/features/academic/api'

interface SectionPickerProps {
  classId: string
  sectionId: string
  onClassChange: (id: string) => void
  onSectionChange: (id: string) => void
  errors?: { classId?: string; sectionId?: string }
  /** Occupancy shown is for the current year; capacity is enforced by the API either way. */
  showOccupancy?: boolean
}

export function SectionPicker({
  classId,
  sectionId,
  onClassChange,
  onSectionChange,
  errors,
  showOccupancy = true,
}: SectionPickerProps) {
  const { t } = useTranslation('students')
  const classes = useQuery(classesQuery())
  const sections = classes.data?.find((c) => String(c.id) === classId)?.sections ?? []
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField label={t('Class')} error={errors?.classId} required>
        <Select
          placeholder={t('Choose a class')}
          value={classId}
          onChange={(e) => {
            onClassChange(e.target.value)
            onSectionChange('')
          }}
          options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
      </FormField>
      <FormField label={t('Section')} error={errors?.sectionId} required>
        <Select
          placeholder={classId ? t('Choose a section') : t('Choose a class first')}
          disabled={!classId}
          value={sectionId}
          onChange={(e) => onSectionChange(e.target.value)}
          options={sections.map((s) => ({
            value: s.id,
            label: `${s.name ? t('Section {{name}}', { name: s.name }) : t('Whole class')}${showOccupancy && s.capacity ? ` · ${s.studentCount}/${s.capacity}` : ''}`,
          }))}
        />
      </FormField>
    </div>
  )
}
